import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { PublicLayout } from "../components/public/PublicLayout";
import { Clock, ChefHat, Users, ChevronLeft } from "lucide-react";

export default function RecipeDetail() {
  const { slug } = useParams();
  const { t, tr } = useLang();
  const navigate = useNavigate();

  const { data: recipe, isLoading } = useQuery({
    queryKey: ["recipe", slug], queryFn: async () => (await api.get(`/recipes/${slug}`)).data,
  });
  const { data: allProducts = [] } = useQuery({
    queryKey: ["products-all"], queryFn: async () => (await api.get("/products")).data,
  });

  useEffect(() => { if (recipe) document.title = `${recipe.title} | Sambiva Recipes`; }, [recipe]);

  if (isLoading) return <PublicLayout><div className="max-w-4xl mx-auto px-6 py-24 text-center text-sambiva-muted">{t("common.loading")}</div></PublicLayout>;
  if (!recipe) return <PublicLayout><div className="max-w-4xl mx-auto px-6 py-24 text-center">Recipe not found.</div></PublicLayout>;

  const related = allProducts.filter((p) => recipe.related_products?.includes(p.sku));

  return (
    <PublicLayout>
      <div className="relative h-[50vh] min-h-[360px] overflow-hidden">
        <img src={recipe.image} alt={recipe.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-sambiva-charcoal/85 to-sambiva-charcoal/20" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 h-full flex flex-col justify-end pb-10">
          <button onClick={() => navigate("/recipes")} className="flex items-center gap-1 text-white/90 hover:text-sambiva-yellow mb-4 text-sm w-fit">
            <ChevronLeft size={16} /> Recipes
          </button>
          <h1 className="font-serif text-4xl sm:text-5xl text-white mb-3">{tr(recipe.translations, "title", recipe.title)}</h1>
          <div className="flex flex-wrap gap-5 text-white/90 text-sm">
            <span className="flex items-center gap-1.5"><Clock size={16} /> Prep {recipe.prep_time}</span>
            {recipe.cook_time && <span className="flex items-center gap-1.5"><Clock size={16} /> Cook {recipe.cook_time}</span>}
            <span className="flex items-center gap-1.5"><ChefHat size={16} /> {recipe.difficulty}</span>
            <span className="flex items-center gap-1.5"><Users size={16} /> {recipe.servings} servings</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14">
        <p className="text-lg text-sambiva-muted leading-relaxed mb-10">{recipe.description}</p>
        <div className="grid md:grid-cols-[280px_1fr] gap-10">
          <div>
            <h2 className="font-serif text-2xl text-sambiva-charcoal mb-4">Ingredients</h2>
            <ul className="space-y-2">
              {recipe.ingredients?.map((ing, i) => (
                <li key={i} className="flex gap-2 text-sambiva-charcoal">
                  <span className="text-sambiva-red">•</span> {ing}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="font-serif text-2xl text-sambiva-charcoal mb-4">Method</h2>
            <ol className="space-y-5">
              {recipe.steps?.map((step, i) => (
                <li key={i} className="flex gap-4">
                  <span className="shrink-0 w-8 h-8 rounded-full bg-sambiva-red text-white flex items-center justify-center font-semibold text-sm">{i + 1}</span>
                  <p className="text-sambiva-charcoal pt-1">{step}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-16">
            <h2 className="font-serif text-2xl text-sambiva-charcoal mb-6">Made with Sambiva</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {related.map((p) => (
                <div key={p.id} onClick={() => navigate(`/products/${p.slug}`)} className="bg-white rounded-2xl overflow-hidden card-hover cursor-pointer border border-sambiva-charcoal/5">
                  <div className="aspect-square"><img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover" /></div>
                  <div className="p-3"><p className="font-serif text-sm text-sambiva-charcoal">{p.name}</p></div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
